import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { X, Loader2 } from "lucide-react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";

const defaultValues = {
  courseId: "",
  courseName: "",
  batchName: "",
  startDate: "",
  endDate: "",
  timing: "",
  days: "",
  status: "upcoming",
  mode: "offline",
  seatsLeft: 0,
  instructorId: "",
  instructorName: "",
  whatsappGroupLink: "",
};

// "2026-10-06T00:00:00.000Z" -> "2026-10-06" (what <input type="date"> needs)
const toInputDate = (v) => {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

// Edit mode: turn the saved batch into form values so EVERY field is
// pre-filled (dates converted, populated course/instructor objects flattened).
const buildInitialValues = (data) => {
  if (!data) return defaultValues;
  const course = data.courseId && typeof data.courseId === "object" ? data.courseId : null;
  const instructor =
    data.instructorId && typeof data.instructorId === "object" ? data.instructorId : null;
  return {
    ...defaultValues,
    ...data,
    courseId: course?._id || data.courseId || "",
    courseName: data.courseName || course?.courseName || "",
    batchName: data.batchName || "",
    startDate: toInputDate(data.startDate),
    endDate: toInputDate(data.endDate),
    timing: data.timing || "",
    days: data.days || "",
    status: data.status || "upcoming",
    mode: data.mode || "offline",
    seatsLeft: data.seatsLeft ?? 0,
    instructorId: instructor?._id || data.instructorId || "",
    instructorName: data.instructorName || instructor?.name || "",
    whatsappGroupLink: data.whatsappGroupLink || "",
  };
};

function AddBatchForm({ onSuccess, onCancel, initialData }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm({
    defaultValues: buildInitialValues(initialData),
  });

  const isEditing = !!initialData?._id;
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [coursesError, setCoursesError] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);
  const [instructorsError, setInstructorsError] = useState(null);

  useEffect(() => {
    reset(buildInitialValues(initialData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?._id]);

  useEffect(() => {
    const loadCourses = async () => {
      setLoadingCourses(true);
      setCoursesError(null);
      try {
        const res = await axiosInstance.get(apiUrl.GETCOURSES);
        if (res.data?.status && Array.isArray(res.data?.data)) {
          setCourses(res.data.data);
        } else {
          setCourses([]);
        }
      } catch (error) {
        setCoursesError(
          error.response?.data?.message || "Failed to load courses"
        );
        setCourses([]);
      } finally {
        setLoadingCourses(false);
      }
    };

    loadCourses();
  }, []);

  useEffect(() => {
    const loadInstructors = async () => {
      setLoadingInstructors(true);
      setInstructorsError(null);
      try {
        const res = await axiosInstance.get(
          `${apiUrl.GET_INSTRUCTORS}?active=true`
        );
        if (res.data?.status && Array.isArray(res.data?.data)) {
          setInstructors(res.data.data);
        } else {
          setInstructors([]);
        }
      } catch (error) {
        setInstructorsError(
          error.response?.data?.message || "Failed to load instructors"
        );
        setInstructors([]);
      } finally {
        setLoadingInstructors(false);
      }
    };

    loadInstructors();
  }, []);

  // Datalist input: user types or picks a name -> find the matching course
  // and keep courseId in sync (cleared if the text matches nothing).
  const handleCourseChange = (e) => {
    const text = e.target.value;
    setValue("courseName", text, { shouldValidate: true });
    const match = courses.find(
      (c) => (c.courseName || "").trim().toLowerCase() === text.trim().toLowerCase()
    );
    setValue("courseId", match?._id || "", { shouldValidate: true });
    if (match) setValue("courseName", match.courseName || text);
  };

  const handleInstructorChange = (e) => {
    const text = e.target.value;
    setValue("instructorName", text, { shouldValidate: true });
    const match = instructors.find(
      (i) => (i.name || "").trim().toLowerCase() === text.trim().toLowerCase()
    );
    setValue("instructorId", match?._id || "", { shouldValidate: true });
    if (match) setValue("instructorName", match.name || text);
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        seatsLeft: Number(values.seatsLeft) || 0,
        // Backend casts instructorId to ObjectId — "" crashes it, so send
        // null when no instructor is chosen (also clears it on edit).
        instructorId: values.instructorId || null,
        instructorName: values.instructorId ? values.instructorName : "",
      };

      if (isEditing) {
        await axiosInstance.patch(
          `${apiUrl.UPDATE_BATCH}/${initialData._id}`,
          payload
        );
      } else {
        await axiosInstance.post(apiUrl.POST_BATCH, payload);
      }

      reset(defaultValues);
      if (onSuccess) onSuccess();
    } catch (error) {
      const msg =
        error.response?.data?.message || "Failed to save batch. Please try again.";
      alert(msg);
    }
  };

  return (
    <div className="mx-auto max-w-3xl rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-200">
            {isEditing ? "Edit batch" : "Add new batch"}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Configure course batch details for offline / online schedules.
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#13233a] text-slate-500 hover:bg-[#1c2740] hover:text-slate-300"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Course
            </label>
            <input type="hidden" {...register("courseId", { required: "Please pick a course from the list" })} />
            <input
              type="text"
              list="course-options"
              autoComplete="off"
              {...register("courseName", { required: "Course is required" })}
              onChange={handleCourseChange}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              placeholder={loadingCourses ? "Loading courses..." : "Type or select course"}
            />
            <datalist id="course-options">
              {courses.map((course) => (
                <option key={course._id} value={course.courseName} />
              ))}
            </datalist>
            {coursesError && (
              <p className="mt-1 text-xs text-red-400">{coursesError}</p>
            )}
            {(errors.courseName || errors.courseId) && (
              <p className="mt-1 text-xs text-red-400">
                {errors.courseId?.message || errors.courseName?.message}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Instructor
            </label>
            <input type="hidden" {...register("instructorId")} />
            <input
              type="text"
              list="instructor-options"
              autoComplete="off"
              {...register("instructorName")}
              onChange={handleInstructorChange}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              placeholder={loadingInstructors ? "Loading instructors..." : "Type or select instructor (optional)"}
            />
            <datalist id="instructor-options">
              {instructors.map((inst) => (
                <option key={inst._id} value={inst.name} />
              ))}
            </datalist>
            {instructorsError && (
              <p className="mt-1 text-xs text-red-400">{instructorsError}</p>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Batch name
            </label>
            <input
              type="text"
              {...register("batchName", { required: "Batch name is required" })}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              placeholder="e.g. Batch FE-01"
            />
            {errors.batchName && (
              <p className="mt-1 text-xs text-red-400">
                {errors.batchName.message}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Seats left
            </label>
            <input
              type="number"
              min={0}
              {...register("seatsLeft", { valueAsNumber: true })}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-1">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              WhatsApp group link (students will see a "Join Group" button)
            </label>
            <input
              type="url"
              {...register("whatsappGroupLink")}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              placeholder="https://chat.whatsapp.com/..."
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Start date
            </label>
            <input
              type="date"
              {...register("startDate", { required: "Start date is required" })}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
            />
            {errors.startDate && (
              <p className="mt-1 text-xs text-red-400">
                {errors.startDate.message}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              End date
            </label>
            <input
              type="date"
              {...register("endDate", { required: "End date is required" })}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
            />
            {errors.endDate && (
              <p className="mt-1 text-xs text-red-400">
                {errors.endDate.message}
              </p>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Timing
            </label>
            <input
              type="text"
              {...register("timing", { required: "Timing is required" })}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              placeholder="10:00 AM – 1:00 PM"
            />
            {errors.timing && (
              <p className="mt-1 text-xs text-red-400">
                {errors.timing.message}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Days
            </label>
            <input
              type="text"
              {...register("days", { required: "Days are required" })}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              placeholder="Mon, Wed, Fri"
            />
            {errors.days && (
              <p className="mt-1 text-xs text-red-400">{errors.days.message}</p>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Status
            </label>
            <select
              {...register("status")}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
            >
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Mode
            </label>
            <select
              {...register("mode")}
              className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
            >
              <option value="offline">Offline</option>
              <option value="online">Online</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2"> 
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[#1d3350] px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-[#0d1b2e]"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-700 disabled:opacity-60"
          >
            {isSubmitting && <Loader2 size={16} className="animate-spin" />}
            {isEditing ? "Save changes" : "Create batch"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddBatchForm;